import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage';
import {
  HttpBackend,
  HttpClient,
  HttpEvent,
  HttpEventType,
  HttpHeaders,
} from '@angular/common/http';
import {
  BehaviorSubject,
  catchError,
  filter,
  firstValueFrom,
  map,
  Observable,
  retry,
  timer,
} from 'rxjs';
import { environment } from 'sac-mobile-v3/src/environments/environment';

// ──────────────────────────────────────────────
//  Types
// ──────────────────────────────────────────────

export interface ReadingValue {
  sequence?: number;
  value: any;
  fileName?: string;
}

/** Images stored in ionic storage as base64 strings on meter.image[] */
export interface StoredImage {
  image: string; // base64 or data-url string
}

export interface MeterReading {
  meterId: string;
  propertyId: string;
  readingBy: string;
  readingTS: string;
  readingValueList: ReadingValue[];
  readingType?: string;
  readingPeriod?: string;
  type?: string;
  fileName?: string;
  comment?: string;
  unitId?: string;
  image?: StoredImage[];       // base64 images from ionic storage
  meterNumber?: string;
  captured?: string;
  [key: string]: any; // allow extra fields from storage
}

export interface FaultyMeterReport {
  comment: string;
  meterId: string;
  propertyId: string;
  unitId: string;
}

export interface ImageUploadResult {
  sequence: number;
  success: boolean;
  error?: string;
}

export interface SyncResult {
  success: boolean;
  meterId: string;
  failedUploads?: ImageUploadResult[];
  captureResponse?: any;
  error?: string;
}

export interface UploadProgress {
  meterId: string;
  sequence: number;
  percent: number;
}

// ──────────────────────────────────────────────
//  Service
// ──────────────────────────────────────────────

@Injectable({
  providedIn: 'root',
})
export class CaptureReadingService {
  private httpBypass: HttpClient;
  private readonly maxRetries = 3;
  private readonly retryDelayMs = 1000;

  /** Emits granular upload progress per image */
  uploadProgress$ = new BehaviorSubject<UploadProgress | null>(null);

  /** Legacy progress field — kept for backward compat but prefer uploadProgress$ */
  public progress = 0;

  private meterReadingEndPoint = environment.api.meterReading;

  constructor(
    private storage: Storage,
    private httpClient: HttpClient,
    private httpBackend: HttpBackend
  ) {
    this.httpBypass = new HttpClient(httpBackend);
  }

  // ──────────────────────────────────────────
  //  Public – full sync pipeline for one meter
  // ──────────────────────────────────────────

  /**
   * 1. Capture reading via GraphQL
   * 2. Upload every image to S3 (with retries)
   *
   * Returns a typed SyncResult so the caller knows exactly what failed.
   */


  private async tryUpdatePeriodReading(meter: MeterReading): Promise<SyncResult> {
    try {
      const period =
        meter.readingPeriod ||
        localStorage.getItem('currentReadingPeriod') ||
        '';

      const supplierId =
        meter['utilitySupplierId'] ||
        localStorage.getItem('currentUtilitySupplierId') ||
        '';

      const updatePayload = {
        meterId: meter.meterId,
        approvalStatus: 'NEW',
        reading: {
          propertyId: meter.propertyId,
          readingBy: meter.readingBy,
          readingTS: meter.readingTS,
          readingType: meter.readingType || 'PERIOD_READING',
          readingValueList: meter.readingValueList || [],
        },
      };

      const updateRes = await firstValueFrom(
        this.updatePeriodReading(
          updatePayload,
          'Updated during offline sync because a newer period reading already existed.',
          period,
          supplierId
        )
      );

      const payload = updateRes?.data?.updatePeriodReading;

      if (payload?.isSuccessful) {
        return {
          success: true,
          meterId: meter.meterId,
        };
      }

      return {
        success: false,
        meterId: meter.meterId,
        error: 'Update period reading failed',
      };
    } catch (err: any) {
      return {
        success: false,
        meterId: meter.meterId,
        error: `Update period reading failed`,
      };
    }
  }


  async syncMeterReading(meter: MeterReading): Promise<SyncResult> {
    // ── Step 1: Capture the reading ────────────
    console.log(meter)
    try {
      const captureRes = await firstValueFrom(this.captureNewReading(meter));

      let payload = captureRes?.data?.captureMeterReadingSync;
      if (payload && !payload.isSuccessful) {
        if (payload && !payload.isSuccessful && payload.errorMessage === 'Existing Period Reading is newer than new Candidate Period Reading') {
          const updateResult = await this.tryUpdatePeriodReading(meter);
          console.log(updateResult)
          if (updateResult && updateResult.success) {
            payload = updateResult
          }
          else {
            return {
              success: false,
              meterId: meter.meterId,
              error: payload.errorMessage || 'Reading capture rejected by server',
            };
          }
        } else {
          return {
            success: false,
            meterId: meter.meterId,
            error: payload.errorMessage || 'Reading capture rejected by server',
          };
        }
      }

    } catch (err: any) {
      console.log(err)
      return {
        success: false,
        meterId: meter.meterId,
        error: `Reading capture failed: ${err?.message || err}`,
      };
    }

    // ── Step 2: Upload images ──────────────────
    const images = meter.image || [];
    if (images.length === 0) {
      return { success: true, meterId: meter.meterId };
    }

    const imageResults: ImageUploadResult[] = [];

    for (let i = 0; i < images.length; i++) {
      const result = await this.uploadSingleImage(meter, images[i].image, i + 1);
      imageResults.push(result);
    }

    const failedUploads = imageResults.filter((r) => !r.success);

    if (failedUploads.length > 0) {
      return {
        success: false,
        meterId: meter.meterId,
        failedUploads,
        error: `${failedUploads.length} of ${images.length} image(s) failed to upload`,
      };
    }

    return { success: true, meterId: meter.meterId };
  }

  // ──────────────────────────────────────────
  //  Private – single image upload pipeline
  // ──────────────────────────────────────────
  private async compressImageBase64(
    base64: string,
    maxWidth: number = 1600,
    quality: number = 0.75
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        const scale = Math.min(1, maxWidth / img.width);
        const canvas = document.createElement('canvas');

        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas context not available'));
          return;
        }

        // white background in case original has transparency
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to compress image'));
              return;
            }
            resolve(blob);
          },
          'image/jpeg',
          quality
        );
      };

      img.onerror = () => reject(new Error('Failed to load image for compression'));

      img.src = base64.startsWith('data:')
        ? base64
        : `data:image/jpeg;base64,${base64}`;
    });
  }
  private async uploadSingleImage(
    meter: MeterReading,
    base64Image: string,
    sequence: number
  ): Promise<ImageUploadResult> {
    try {
      // 1. Get presigned URL
      const presignedRes = await firstValueFrom(
        this.getPresignedURL(meter, sequence)
      );

      const capturePayload = presignedRes?.data?.captureImageReading;
      if (!capturePayload?.isSuccessful || !capturePayload?.presignedURL) {
        return {
          sequence,
          success: false,
          error: 'Failed to obtain presigned upload URL',
        };
      }
      const originalBlob = this.base64ToBlob(base64Image);

      console.log(
        `[Image Upload] Meter ${meter.meterId} Seq ${sequence} - Original size:`,
        (originalBlob.size / 1024).toFixed(2),
        'KB'
      );
      const url: string = capturePayload.presignedURL;

      // 2. Compress image before upload
      let imageBlob: Blob;

      try {
        imageBlob = await this.compressImageBase64(base64Image, 1600, 0.75);

        console.log(
          `[Image Upload] Meter ${meter.meterId} Seq ${sequence} - Compressed size:`,
          (imageBlob.size / 1024).toFixed(2),
          'KB'
        );

        const reduction =
          ((originalBlob.size - imageBlob.size) / originalBlob.size) * 100;

        console.log(
          `[Image Upload] Reduction: ${reduction.toFixed(1)}%`
        );

      } catch (e) {
        console.warn('[Upload] Compression failed, uploading original image instead', e);
        imageBlob = originalBlob;
      }
      // 3. PUT to S3
      await firstValueFrom(
        this.uploadImage(url, imageBlob, meter.meterId, sequence).pipe(
          filter((event: HttpEvent<any>) => event.type === HttpEventType.Response)
        )
      );

      return { sequence, success: true };
    } catch (err: any) {
      return {
        sequence,
        success: false,
        error: err?.message || 'Image upload failed',
      };
    }
  }

  // ──────────────────────────────────────────
  //  GraphQL – capture reading
  // ──────────────────────────────────────────
  getReading(): Promise<any> {
    return this.storage.get('newmeters');
  }

  async prepareImageForUpload(
    base64Image: string,
    meterId?: string,
    sequence?: number
  ): Promise<Blob> {
    const originalBlob = this.base64ToBlob(base64Image);

    console.log(
      `[Image Upload] Meter ${meterId || 'unknown'} Seq ${sequence || '-'} - Original size:`,
      (originalBlob.size / 1024).toFixed(2),
      'KB'
    );

    try {
      const compressedBlob = await this.compressImageBase64(base64Image, 1600, 0.75);

      console.log(
        `[Image Upload] Meter ${meterId || 'unknown'} Seq ${sequence || '-'} - Compressed size:`,
        (compressedBlob.size / 1024).toFixed(2),
        'KB'
      );

      const reduction =
        ((originalBlob.size - compressedBlob.size) / originalBlob.size) * 100;

      console.log(
        `[Image Upload] Reduction: ${reduction.toFixed(1)}%`
      );

      return compressedBlob;
    } catch (e) {
      console.warn('[Upload] Compression failed, uploading original image instead', e);
      return originalBlob;
    }
  }
  updatePeriodReading(
    reading: any,
    reason: string,
    period: string,
    supplierId: string
  ): Observable<any> {
    const copy = JSON.parse(JSON.stringify(reading));

    copy.reading.readingValueList = (copy.reading?.readingValueList || []).map(
      (v: any, i: number) => ({
        sequence: v.sequence ?? i + 1,
        value: v.value,
        category: v.category ?? '',
        imageKey: v.imageKey ?? '',
        name: v.name ?? '',
      })
    );

    const gqlQuery = `
    mutation MyMutation(
      $utilitySupplierId: String = "",
      $meterId: String = "",
      $propertyId: String = "",
      $readingPeriod: String = "",
      $meterId1: String = "",
      $readingTS: AWSDateTime!,
      $propertyId1: String = "",
      $comment: String = "",
      $readingBy: String = "",
      $approvalStatus: ApprovalStatus = NEW,
      $readingType: ReadingType = PERIOD_READING,
      $readingValueList: [ReadingValueInput]
    ) {
      updatePeriodReading(
        utilitySupplierId: $utilitySupplierId,
        meterId: $meterId,
        propertyId: $propertyId,
        reading: {
          meterId: $meterId1,
          readingTS: $readingTS,
          propertyId: $propertyId1,
          comment: $comment,
          readingBy: $readingBy,
          readingType: $readingType,
          readingValueList: $readingValueList
        },
        readingPeriod: $readingPeriod,
        approvalStatus: $approvalStatus
      ) {
        isSuccessful
      }
    }
  `;

    const body = {
      query: gqlQuery,
      variables: {
        approvalStatus: copy.approvalStatus || 'NEW',
        meterId: copy.meterId,
        meterId1: copy.meterId,
        propertyId: copy.reading?.propertyId,
        propertyId1: copy.reading?.propertyId,
        readingBy: copy.reading?.readingBy,
        readingTS: copy.reading?.readingTS,
        readingValueList: copy.reading?.readingValueList || [],
        comment: reason || '',
        readingPeriod: period,
        utilitySupplierId: supplierId,
        readingType: copy.reading?.readingType || 'PERIOD_READING',
      },
    };

    return this.httpClient.post(environment.api.adminCapture, body).pipe(
      retry({
        count: 2,
        delay: (_err, attempt) => timer(this.retryDelayMs * attempt),
      }),
      catchError((err) => {
        console.error('[CaptureReadingService] updatePeriodReading failed:', err);
        throw err;
      })
    );
  }
  captureNewReading(meter: MeterReading): Observable<any> {
    const copy: MeterReading = JSON.parse(JSON.stringify(meter));

    // Normalise readingValueList → { sequence, value }
    copy.readingValueList = (copy.readingValueList || []).map(
      (v: ReadingValue, i: number) => ({
        sequence: i + 1,
        value: v.value,
      })
    );

    const gqlQuery = `
      mutation MyMutation(
        $meterId: String = "",
        $propertyId: String = "",
        $readingBy: String = "",
        $readingTS: AWSDateTime!,
        $readingValueList: [ReadingValueInput],
        $readingType: ReadingType = PERIOD_READING
      ) {
        captureMeterReadingSync(reading: {
          meterId: $meterId,
          isDecommissionMeterReading: false,
          propertyId: $propertyId,
          readingBy: $readingBy,
          readingTS: $readingTS,
          readingValueList: $readingValueList,
          readingType: $readingType,
          comment: ""
        }) {
          isSuccessful
          flag
          errorMessage
        }
      }
    `;

    const body = {
      query: gqlQuery,
      variables: {
        isDecommissionMeterReading: false,
        meterId: copy.meterId,
        propertyId: copy.propertyId,
        readingBy: copy.readingBy,
        readingTS: copy.readingTS,
        readingValueList: copy.readingValueList,
        readingType: copy.readingType || 'PERIOD_READING',
        comment: '',
      },
    };

    return this.httpClient.post(environment.api.adminCapture, body).pipe(
      retry({
        count: 2,
        delay: (_err, attempt) => timer(this.retryDelayMs * attempt),
      }),
      catchError((err) => {
        console.error('[CaptureReadingService] captureNewReading failed:', err);
        throw err;
      })
    );
  }

  // ──────────────────────────────────────────
  //  S3 – image upload with progress + retry
  // ──────────────────────────────────────────

  uploadImage(
    url: string,
    imageBlob: Blob,
    meterId?: string,
    sequence?: number
  ): Observable<HttpEvent<any>> {
    const headers = new HttpHeaders({
      'Content-Type': imageBlob.type || 'image/jpeg',
    });

    return this.httpBypass
      .put(url, imageBlob, {
        headers,
        reportProgress: true,
        observe: 'events',
      })
      .pipe(
        map((event: HttpEvent<any>) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            const percent = Math.round((100 * event.loaded) / event.total);
            this.progress = percent;
            if (meterId && sequence) {
              this.uploadProgress$.next({ meterId, sequence, percent });
            }
          }
          return event;
        }),
        retry({
          count: this.maxRetries,
          delay: (error: any, retryCount: number) => {
            console.warn(
              `[Upload] Retry ${retryCount}/${this.maxRetries}` +
              (meterId ? ` for meter ${meterId} seq ${sequence}` : ''),
              error?.message || error
            );
            return timer(this.retryDelayMs * retryCount);
          },
        }),
        catchError((error: any) => {
          console.error('[Upload] All retries exhausted:', error);
          throw error;
        })
      );
  }

  // ──────────────────────────────────────────
  //  GraphQL – presigned URL
  // ──────────────────────────────────────────

  getPresignedURL(capture: any, sequence: number): Observable<any> {
    const query = `
      mutation MyMutation(
        $filename: String = "",
        $meterId: String = "",
        $propertyId: String = "",
        $readingBy: String = "",
        $readingTS: AWSDateTime!,
        $agentCode: String = "",
        $sequence: String = "",
        $readingPeriod: String = ""
      ) {
        captureImageReading(reading: {
          filename: $filename,
          meterId: $meterId,
          propertyId: $propertyId,
          readingBy: $readingBy,
          readingTS: $readingTS,
          agentCode: $agentCode,
          sequence: $sequence,
          readingPeriod: $readingPeriod
        }) {
          presignedURL
          isSuccessful
        }
      }
    `;

    const variables = {
      filename: capture.fileName || '',
      meterId: capture.meterId,
      propertyId: capture.propertyId,
      readingBy: capture.readingBy,
      readingTS: capture.readingTS,
      agentCode: '0000',
      sequence: String(sequence),
      readingPeriod: capture.readingPeriod || '',
    };

    return this.httpClient
      .post(this.meterReadingEndPoint, { query, variables })
      .pipe(
        retry({
          count: 2,
          delay: (_err, attempt) => timer(this.retryDelayMs * attempt),
        }),
        catchError((error) => {
          console.error('[CaptureReadingService] getPresignedURL failed:', error);
          throw new Error('Failed to get upload URL. Please check your connection.');
        })
      );
  }

  // ──────────────────────────────────────────
  //  GraphQL – faulty meter report
  // ──────────────────────────────────────────

  captureFaultyReading(meter: FaultyMeterReport): Observable<any> {
    const data = {
      query: `
        mutation MyMutation(
          $message: String = "",
          $meterId: String = "",
          $propertyId: String = "",
          $unitId: String = ""
        ) {
          addFaultyMeterNotification(
            message: $message,
            meterId: $meterId,
            propertyId: $propertyId,
            unitId: $unitId
          ) {
            presignedUrl
          }
        }
      `,
      variables: {
        message: meter.comment,
        meterId: meter.meterId,
        propertyId: meter.propertyId,
        unitId: meter.unitId,
      },
    };

    return this.httpClient.post(environment.api.bigCall, data);
  }

  // ──────────────────────────────────────────
  //  Utility
  // ──────────────────────────────────────────

  blobToBase64DataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Failed to convert blob to base64'));
      reader.readAsDataURL(blob);
    });
  }

  private base64ToBlob(base64: string): Blob {
    let base64Data: string;
    let mimeType = 'image/png';

    if (base64.startsWith('data:')) {
      const [header, data] = base64.split(',');
      base64Data = data;
      const mimeMatch = header.match(/data:([^;]+)/);
      if (mimeMatch) mimeType = mimeMatch[1];
    } else {
      base64Data = base64;
    }

    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    return new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
  }
}